import InfluenzaDetailView from 'routes/apps/influenza/Candidates'

import { ValueType } from 'interfaces/valueType.interface'

import { SortOrders } from 'enums/SortOrders'

import {
  ConferencePresentationsModel,
  MediaModel,
  PressReleasesModel,
} from 'config/common/baseModel'
import { ApprovalsModel } from 'config/common/baseModel/Approvals'
import { ClinicalTrialsModel } from 'config/common/baseModel/ClinicalTrials'
import { PresentationsModel } from 'config/common/baseModel/Presentations'
import {
  RespiratoryCandidatesAllCombinedModel,
  RespiratoryCombinationVaccinesModel,
  RespiratoryTreatmentsModel,
  RespiratoryVaccinesModel,
} from 'config/common/baseModel/respiratory360/RespiratoryCandidates'
import { RespiratoryResultsModel } from 'config/common/baseModel/respiratory360/RespiratoryResults'

import {
  compose,
  renameColumn,
  updateColumnAttributes,
  whiteListColumn,
  blackListColumn,
} from 'utils/overrideModel'

export const InfluenzaCandidatesVaccineModel = compose<
  typeof RespiratoryVaccinesModel
>(blackListColumn(['targetPopulation']))({
  ...RespiratoryVaccinesModel,
  renderDetailView: InfluenzaDetailView,
})

export const InfluenzaCandidatesNonVaccinesModels = compose<
  typeof RespiratoryTreatmentsModel
>(blackListColumn(['targetPopulation']))({
  ...RespiratoryTreatmentsModel,
  renderDetailView: InfluenzaDetailView,
})

export const InfluenzaCandidatesCombinationVaccinesModel = compose<
  typeof RespiratoryCombinationVaccinesModel
>(blackListColumn(['targetPopulation']))({
  ...RespiratoryCombinationVaccinesModel,
  renderDetailView: InfluenzaDetailView,
})

export const InfluenzaCandidatesAllCombinedModel = {
  ...RespiratoryCandidatesAllCombinedModel,
  renderDetailView: InfluenzaDetailView,
}

export const InfluenzaCandidatePapersModel = compose<typeof PresentationsModel>(
  whiteListColumn([
    'title',
    'journalTitle',
    'publicationDate',
    'candidateNameFromVaccinesCandidateList',
    'candidateNameFromTreatmentsCandidateList',
    'articleTypesSpecific',
    'lookupJournalImpactScore',
    'articleLink',
  ]),
  updateColumnAttributes('afTopics', { type: ValueType.SINGLE }),
  updateColumnAttributes('candidateNameFromVaccinesCandidateList', {
    graphType: 'Bar',
  }),
  updateColumnAttributes('candidateNameFromTreatmentsCandidateList', {
    graphType: 'Bar',
  }),
  updateColumnAttributes('afTopics', { graphType: 'Pie' })
)({
  ...PresentationsModel,
  name: 'Publications',
  chartColumnBlackList: [
    'title',
    'journalTitle',
    'articleTypesSpecific',
    'publicationDate',
    'afEfficacySentiment',
    'afTags',
    'concatOrganisations',
    'targetEnrollmentFromClinicalTrials',
    'articleViews',
    'pdfDownloads',
    'lookupJournalImpactScore',
    'tweetMentions',
    'socialScore',
  ],
  endpoint: 'publication',
  detailView: {
    link: 'doiUrl',
    abstract: 'abstract',
  },
  exportDisabled: true,
})

export const InfluenzaConferencePresentionsModel = compose<
  typeof ConferencePresentationsModel
>(
  whiteListColumn([
    'startDate',
    'eventName',
    'sessionType',
    'titleClean',
    'descriptionClean',
    'candidateNameFromLinkVaccineCandidate',
    'candidateNameFromLinkTreatmentCandidate',
    'afSentiment',
    'sessionTypeKey',
  ]),
  renameColumn('startDate', 'Date'),
  updateColumnAttributes('sessionType', { graphType: 'Pie' }),
  updateColumnAttributes('candidateNameFromLinkVaccineCandidate', {
    graphType: 'Bar',
  }),
  updateColumnAttributes('candidateNameFromLinkTreatmentCandidate', {
    graphType: 'Bar',
  }),
  renameColumn('candidateNameFromLinkTreatmentCandidate', 'Non-Vaccines')
)({
  ...ConferencePresentationsModel,
  exportDisabled: true,
  chartColumnBlackList: [
    'startDate',
    'eventName',
    'titleClean',
    'descriptionClean',
    'afSentiment',
    'sessionTypeKey',
  ],
  name: 'Conferences',
  detailView: {
    abstract: 'descriptionClean',
  },
  displayKey: 'titleClean',
  defaultSortObject: [
    { id: 'eventName', sortOrder: SortOrders.DESC },
    { id: 'sessionTypeKey', sortOrder: SortOrders.ASC },
  ],
})

export const InfluenzaTrialsModel = compose<typeof ClinicalTrialsModel>(
  whiteListColumn([
    'id',
    'title',
    'diseaseName',
    'acronym',
    'candidates',
    'sponsors',
    'latestPhaseFinal',
    'status',
    'startDate',
    'enroledAt',
    'primaryCompletionDate',
    'completedAt',
    'targetEnrolment',
    'numberOfParticipants',
    'studyType',
    'ages',
    'rawPrimaryOutcomeMeasures',
    'areas',
    'url',
  ]),
  renameColumn('latestPhaseFinal', 'Phase'),
  updateColumnAttributes('latestPhaseFinal', { graphType: 'Bar' }),
  updateColumnAttributes('candidates', {
    graphType: 'Bar',
  }),
  updateColumnAttributes('areas', { graphType: 'Bar' })
)({
  ...ClinicalTrialsModel,
  endpoint: 'clinical-trial',
  chartColumnBlackList: [
    'id',
    'eidList',
    'title',
    'acronym',
    'status',
    'url',
    'conditions',
    'interventions',
    'targetEnrolment',
    'studyType',
    'studyDesign',
    'ages',
    'gender',
    'enroledAt',
    'fStudyCompletion',
    'rawPrimaryOutcomeMeasures',
    'sponsors',
    'collaborators',
    'outcomeMeasures',
  ],
  defaultSortOrder: SortOrders.DESC,
  defaultSortKey: 'enroledAt',
  searchField: 'clinicalTrialId',
  displayKey: 'clinicalTrialId',
  // excludeGeneratedCharts: true,
})

export const InfluenzaPressReleasesModel = compose<typeof PressReleasesModel>(
  whiteListColumn([
    'title',
    'linkInfectiousDisease',
    'datePublished',
    'afTopic',
    'link',
    'candidateNameFromVaccinesCandidateList',
    'candidateNameFromTreatmentsCandidateList',
    'companies',
    'afTags',
  ]),
  updateColumnAttributes('candidateNameFromVaccinesCandidateList', {
    graphType: 'Bar',
  }),
  updateColumnAttributes('candidateNameFromTreatmentsCandidateList', {
    graphType: 'Bar',
  }),
  renameColumn(
    'candidateNameFromTreatmentsCandidateList',
    'Non-Vaccines Candidate List'
  ),
  updateColumnAttributes('afTopic', { graphType: 'Pie' })
)({
  ...PressReleasesModel,
  chartColumnBlackList: [
    'title',
    'linkInfectiousDisease',
    'datePublished',
    'link',
    'companies',
    'afTags',
  ],
})
export const InfluenzaMediaModel = compose<typeof MediaModel>(
  whiteListColumn([
    'title',
    'linkInfectiousDisease',
    'companies',
    'afTopic',
    'datePublished',
    'link',
    'candidateNameFromVaccinesCandidateList',
    'candidateNameFromTreatmentsCandidateList',
    'company',
    'afTags',
  ]),
  updateColumnAttributes('afTopic', { graphType: 'Pie' }),
  updateColumnAttributes('candidateNameFromVaccinesCandidateList', {
    graphType: 'Bar',
  }),
  updateColumnAttributes('candidateNameFromTreatmentsCandidateList', {
    graphType: 'Bar',
  }),
  renameColumn(
    'candidateNameFromTreatmentsCandidateList',
    'Non-Vaccines Candidate List'
  ),
  renameColumn('company', 'Companies Mentioned'),
  renameColumn('companies', 'Media Provider')
)({
  ...MediaModel,
  chartColumnBlackList: [
    'title',
    'linkInfectiousDisease',
    'companies',
    'datePublished',
    'link',
    'language',
    'author',
    'company',
    'companyResearchInstitution',
    'afTags',
  ],
})

export const NonVaccinesResultsModel = compose<typeof RespiratoryResultsModel>(
  whiteListColumn([
    'candidateNameFromNotes',
    'lookupManualLatestPhase',
    'efficacyEndpoint',
    'otherEfficacyMeasures',
    'url',
    'sourceType',
    'safetyEndpoint',
    'population',
  ])
)(RespiratoryResultsModel)

export const VaccinesResultsModel = compose<typeof RespiratoryResultsModel>(
  whiteListColumn([
    'candidateNameFromNotes',
    'lookupManualLatestPhase',
    'immunogenicityEndpoint',
    'seroconversion',
    'ninetyFiveCiUpper',
    'ninetyFiveCiLower',
    'gmtTitres',
    'url',
    'sourceType',
    'population',
  ])
)(RespiratoryResultsModel)

export const InfluenzaApprovalsModel = compose<typeof ApprovalsModel>(
  whiteListColumn([
    'candidate',
    'sponsors',
    'targetPopulations',
    'type',
    'area',
    'regulatoryOrganisation',
    'associatedDocumentUrl',
    'approvalDate',
  ])
)({ ...ApprovalsModel })
